import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { SvgPiggyBank } from '@actual-app/components/icons/v1';
import { Popover } from '@actual-app/components/popover';
import { styles } from '@actual-app/components/styles';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import type { AccountEntity } from '@actual-app/core/types/models';

import { useMoveAccountMutation } from '#accounts';
import { isAccountFailedSync } from '#accounts/syncStatus';
import { useAccounts } from '#hooks/useAccounts';
import { useClosedAccounts } from '#hooks/useClosedAccounts';
import { useLocalPref } from '#hooks/useLocalPref';
import { useOffBudgetAccounts } from '#hooks/useOffBudgetAccounts';
import { useOnBudgetAccounts } from '#hooks/useOnBudgetAccounts';
import { useUpdatedAccounts } from '#hooks/useUpdatedAccounts';
import { useSelector } from '#redux';
import * as bindings from '#spreadsheet/bindings';

import { Account } from './Account';
import { Item } from './Item';
import { SecondaryItem } from './SecondaryItem';
import { useSidebar } from './SidebarProvider';

const fontWeight = 600;

export function Accounts() {
  const { t } = useTranslation();
  const sidebar = useSidebar();
  const isCollapsed = sidebar?.collapsed ?? false;
  const [isDragging, setIsDragging] = useState(false);
  const [isPopoverOpen, setPopoverOpen] = useState(false);
  const accountsButtonRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();
  const { data: accounts = [] } = useAccounts();
  const updatedAccounts = useUpdatedAccounts();
  const { data: offbudgetAccounts = [] } = useOffBudgetAccounts();
  const { data: onBudgetAccounts = [] } = useOnBudgetAccounts();
  const { data: closedAccounts = [] } = useClosedAccounts();
  const syncingAccountIds = useSelector(state => state.account.accountsSyncing);

  const getAccountPath = (account: AccountEntity) => `/accounts/${account.id}`;

  const [showClosedAccounts, setShowClosedAccountsPref] = useLocalPref(
    'ui.showClosedAccounts',
  );

  function onDragChange(drag: { state: string }) {
    setIsDragging(drag.state === 'start');
  }

  const moveAccount = useMoveAccountMutation();

  const makeDropPadding = (i: number) => {
    if (i === 0) {
      return {
        paddingTop: isDragging ? 15 : 0,
        marginTop: isDragging ? -15 : 0,
      };
    }
    return undefined;
  };

  async function onReorder(
    id: string,
    dropPos: 'top' | 'bottom' | null,
    targetId: string,
  ) {
    let targetIdToMove: string | null = targetId;
    if (dropPos === 'bottom') {
      const idx = accounts.findIndex(a => a.id === targetId) + 1;
      targetIdToMove = idx < accounts.length ? accounts[idx].id : null;
    }

    moveAccount.mutate({ id, targetId: targetIdToMove });
  }

  const onToggleClosedAccounts = () => {
    setShowClosedAccountsPref(!showClosedAccounts);
  };

  const accountList = (
    <View style={{ overflow: 'auto' }}>
      <Account
        name={t('All accounts')}
        to="/accounts"
        query={bindings.allAccountBalance()}
        style={{ fontWeight, marginTop: 15 }}
        isExactPathMatch
        balanceTestId="sidebar-all-accounts-balance"
      />

      {onBudgetAccounts.length > 0 && (
        <Account
          name={t('On budget')}
          to="/accounts/onbudget"
          query={bindings.onBudgetAccountBalance()}
          style={{
            fontWeight,
            marginTop: 13,
            marginBottom: 5,
          }}
          titleAccount
          balanceTestId="sidebar-on-budget-balance"
        />
      )}

      {onBudgetAccounts.map((account, i) => (
        <Account
          key={account.id}
          name={account.name}
          account={account}
          connected={!!account.bank}
          pending={syncingAccountIds.includes(account.id)}
          failed={isAccountFailedSync(account)}
          updated={updatedAccounts.includes(account.id)}
          to={getAccountPath(account)}
          query={bindings.accountBalance(account.id)}
          onDragChange={onDragChange}
          onDrop={onReorder}
          outerStyle={makeDropPadding(i)}
        />
      ))}

      {offbudgetAccounts.length > 0 && (
        <Account
          name={t('Off budget')}
          to="/accounts/offbudget"
          query={bindings.offBudgetAccountBalance()}
          style={{
            fontWeight,
            marginTop: 13,
            marginBottom: 5,
          }}
          titleAccount
          balanceTestId="sidebar-off-budget-balance"
        />
      )}

      {offbudgetAccounts.map((account, i) => (
        <Account
          key={account.id}
          name={account.name}
          account={account}
          connected={!!account.bank}
          pending={syncingAccountIds.includes(account.id)}
          failed={isAccountFailedSync(account)}
          updated={updatedAccounts.includes(account.id)}
          to={getAccountPath(account)}
          query={bindings.accountBalance(account.id)}
          onDragChange={onDragChange}
          onDrop={onReorder}
          outerStyle={makeDropPadding(i)}
        />
      ))}

      {closedAccounts.length > 0 && (
        <SecondaryItem
          style={{ marginTop: 15 }}
          title={
            showClosedAccounts ? t('Closed accounts') : t('Closed accounts...')
          }
          onClick={onToggleClosedAccounts}
          bold
        />
      )}

      {showClosedAccounts &&
        closedAccounts.map(account => (
          <Account
            key={account.id}
            name={account.name}
            account={account}
            to={getAccountPath(account)}
            query={bindings.accountBalance(account.id)}
            onDragChange={onDragChange}
            onDrop={onReorder}
          />
        ))}
    </View>
  );

  useEffect(() => {
    setPopoverOpen(false);
  }, [location.pathname]);

  if (isCollapsed) {
    return (
      <View style={{ marginTop: 10, flexShrink: 0 }}>
        <Item
          buttonRef={accountsButtonRef}
          title={t('All accounts')}
          Icon={SvgPiggyBank}
          onClick={() => setPopoverOpen(prev => !prev)}
          forceActive={location.pathname.startsWith('/accounts')}
        />
        <Popover
          placement="right top"
          offset={8}
          triggerRef={accountsButtonRef}
          isOpen={isPopoverOpen}
          onOpenChange={open => setPopoverOpen(open)}
          style={{
            width: 280,
            maxHeight: '70vh',
            overflowY: 'auto',
            paddingBottom: 10,
            backgroundColor: theme.sidebarBackground,
            color: theme.sidebarItemText,
            ...styles.darkScrollbar,
          }}
        >
          {accountList}
        </Popover>
      </View>
    );
  }

  return (
    <View
      style={{
        flexGrow: 1,
        '@media screen and (max-height: 480px)': {
          minHeight: 'auto',
        },
      }}
    >
      <View
        style={{
          height: 1,
          backgroundColor: theme.sidebarItemBackgroundHover,
          marginTop: 15,
          flexShrink: 0,
        }}
      />

      {accountList}
    </View>
  );
}
